/*
 * The Foozie: a HELD add to cart (session A3, 2026-09-25).
 *
 * WHY. Horizon 4.2.0's product-form.js QUEUES an add that is pressed while a
 * variant change is still being fetched, and sends it afterwards as
 * {items: [{id, quantity}]} (#processBatchAddToCart). Every other field on the
 * form is dropped: the gift card's recipient (properties[Recipient email],
 * [Recipient name], [Message], [Send on]), a selling plan, and any line-item
 * property a buy box adds, such as Covered Shipping's properties[Order #]. A
 * gift card whose denomination was changed a moment before ADD TO CART would
 * go to the buyer instead of the recipient, with no message.
 *
 * WHAT. An add whose form carries such a field is held here while a variant
 * change is in flight, then submitted again once that change has landed. By
 * then the product form is idle (it clears its in-flight flag in the same
 * task its own await of the change resumes in, and that await was registered
 * first), so it sends the WHOLE form, exactly as an add pressed a second later
 * would. An add with no such field is left to Horizon's queue, which loses
 * nothing for it. Horizon's own file is not edited: its delegated submit
 * handler listens on document in the capture phase, and this listens on
 * window, which runs first.
 *
 * The top of this file is pure, with no DOM and no imports, so
 * bin/test-fz-add-guard.mjs can drive it under Node. The browser wiring is at
 * the bottom.
 */

/** How long a held add waits for a variant change that never settles, in ms. */
export const HOLD_LIMIT_MS = 5000;

/**
 * True when submitting these form entries sends something Horizon's queued
 * add would drop: a non-empty line-item property or selling plan. Disabled
 * inputs never reach FormData, so an unticked "send as a gift" box does not
 * count.
 * @param {Iterable<[string, FormDataEntryValue]>} entries
 */
export function carriesLineFields(entries) {
  for (const [name, value] of entries) {
    if (typeof value !== "string" || value === "") continue;
    if (name.startsWith("properties[") || name === "selling_plan") return true;
  }
  return false;
}

/**
 * The guard's state and decisions, with the browser injected so the seam can
 * be tested: `formDataOf(form)` for the entries, `nextFrame()` to let the new
 * variant's markup settle, `wait(ms)` for the limit.
 * @param {{
 *   formDataOf: (form: any) => Iterable<[string, FormDataEntryValue]>,
 *   nextFrame: () => Promise<void>,
 *   wait: (ms: number) => Promise<void>,
 *   holdLimitMs?: number,
 * }} deps
 */
export function createAddGuard({ formDataOf, nextFrame, wait, holdLimitMs = HOLD_LIMIT_MS }) {
  /** The newest variant change per scope (a section), until it settles. */
  const inflight = new Map();
  /** Forms with an add already held, so a double press is one add, not two. */
  const held = new Set();

  /**
   * Record a variant change. Only the newest per scope counts: an older one
   * is superseded (and usually aborted) by the newer.
   * @param {any} scope
   * @param {Promise<unknown>} promise
   */
  function track(scope, promise) {
    const settled = Promise.resolve(promise).then(
      () => undefined,
      () => undefined,
    );
    inflight.set(scope, settled);
    settled.then(() => {
      if (inflight.get(scope) === settled) inflight.delete(scope);
    });
  }

  /** Wait until no variant change is in flight in `scope`, or the limit passes. */
  async function idle(scope) {
    const deadline = wait(holdLimitMs).then(() => "timeout");
    for (let current = inflight.get(scope); current; current = inflight.get(scope)) {
      if ((await Promise.race([current, deadline])) === "timeout") return;
      await nextFrame();
    }
  }

  /**
   * Decide one submit. Returns 'pass' (let Horizon handle it now), 'held'
   * (prevented; it will be submitted again) or 'duplicate' (prevented; an add
   * for this form is already held).
   * @param {{ form: any, scope: any, submitter?: any, prevent: () => void, resubmit: (submitter?: any) => void }} submit
   */
  function onSubmit({ form, scope, submitter, prevent, resubmit }) {
    if (held.has(form)) {
      prevent();
      return "duplicate";
    }
    if (!inflight.has(scope)) return "pass";
    if (!carriesLineFields(formDataOf(form))) return "pass";
    prevent();
    held.add(form);
    idle(scope).then(() => {
      held.delete(form);
      resubmit(submitter);
    });
    return "held";
  }

  return { track, onSubmit, isInflight: (/** @type {any} */ scope) => inflight.has(scope) };
}

/* ------------------------------------------------------------- the browser */

if (typeof window !== "undefined" && typeof document !== "undefined") {
  const guard = createAddGuard({
    formDataOf: (form) => new FormData(form),
    nextFrame: () => new Promise((resolve) => requestAnimationFrame(() => resolve(undefined))),
    wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  });
  /** @param {Element} el */
  const scopeOf = (el) => el.closest(".shopify-section, dialog, product-card");

  import("@shopify/events").then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.productSelect, (/** @type {any} */ event) => {
      const scope = event.target instanceof Element ? scopeOf(event.target) : null;
      if (scope && event.promise) guard.track(scope, event.promise);
    });
  });

  window.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest("product-form-component")) return;
      const scope = scopeOf(form);
      if (!scope) return;
      guard.onSubmit({
        form,
        scope,
        submitter: /** @type {SubmitEvent} */ (event).submitter,
        prevent: () => {
          event.preventDefault();
          event.stopImmediatePropagation();
        },
        resubmit: (submitter) => {
          if (form.isConnected) form.requestSubmit(submitter && submitter.isConnected ? submitter : undefined);
        },
      });
    },
    { capture: true },
  );
}
